import React from "react";
import { CardBrandIcon } from "./CardBrandIcon";
import { toCardBrand, type PaymentMethod } from "./types";

interface SavedCardItemProps {
  paymentMethod: PaymentMethod;
  onClick: () => void;
}

function ChevronRightIcon(): React.ReactElement {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatExpiry(month: number, year: number): string {
  const mm = String(month).padStart(2, "0");
  return `Exp: ${mm}/${year}`;
}

function formatMaskedNumber(last4: string): string {
  return `•••• •••• •••• ${last4}`;
}

export const SavedCardItem: React.FC<SavedCardItemProps> = ({ paymentMethod, onClick }) => {
  const brand = toCardBrand(paymentMethod.card.brand);
  const holderName = paymentMethod.billingDetails.name || "Card Holder";

  return (
    <div
      className="flex cursor-pointer items-center gap-4 rounded-xl p-4 transition-colors hover:bg-black/[0.02] dark:hover:bg-accent"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Card visual (mini credit card) */}
      <div className="relative flex h-[105px] w-[200px] shrink-0 flex-col justify-between rounded-lg border border-solid border-[#e2e2fc] bg-[#f6f6ff] px-4 py-3 shadow-[0_2px_6px_rgba(39,55,84,0.08)]">
        <div className="flex items-start justify-between">
          <div className="flex items-center justify-center">
            <CardBrandIcon brand={brand} width={48} height={32} />
          </div>
          {paymentMethod.isPrimary && (
            <span className="whitespace-nowrap rounded-lg bg-[#9747ff]/[0.12] px-2 py-[3px] text-sm font-normal leading-[18px] text-[#502489]">
              Primary
            </span>
          )}
          {paymentMethod.source === "org" && (
            <span className="whitespace-nowrap rounded-lg bg-[#1a2547]/[0.08] px-2 py-[3px] text-sm font-normal leading-[18px] text-[#1a2547]">
              Organization
            </span>
          )}
        </div>
        <div className="text-center text-base font-semibold leading-8 tracking-[2px] text-[#1a2547]">
          {formatMaskedNumber(paymentMethod.card.last4)}
        </div>
      </div>

      {/* Card details */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="text-foreground truncate text-base font-bold leading-6">{holderName}</span>
        <span className="text-muted-foreground text-sm font-normal leading-4">
          {formatExpiry(paymentMethod.card.expMonth, paymentMethod.card.expYear)}
        </span>
        {paymentMethod.isExpired && (
          <span className="text-destructive bg-destructive/10 self-start rounded px-2 py-0.5 text-xs font-semibold">
            Expired
          </span>
        )}
      </div>

      {/* Chevron */}
      <div className="text-foreground flex h-6 w-6 shrink-0 items-center justify-center">
        <ChevronRightIcon />
      </div>
    </div>
  );
};
